import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import { alpha } from '@mui/material/styles';
import COLORS from '../../../constants/colors';
import { SectionCard } from '../../../components/Section';
import { GroupRuleValues, PlanRulesDraft, PlanRulesGroup, WeightsRule } from '../../../types/planRules';
import { GEO_LABELS, WEIGHTS_LABELS } from './premium.constants';
import { freeUserSentence, OPEN_GROUP_RULE, planLabel, ruleKey } from './premium.helpers';
import { LimitControl } from './LimitControl';

interface ProductGroupsProps {
  draft: PlanRulesDraft;
  productTypes: Array<{ id: number; name: string }>;
  selectedGroupId: number | undefined;
  onSelectGroup: (groupId: number) => void;
  onGroupChange: (groupId: number, patch: Partial<PlanRulesGroup>) => void;
  /** undefined = remove the rules of this plan in this group. */
  onRuleChange: (planId: number, groupId: number, values: GroupRuleValues | undefined) => void;
  onMoveProduct: (productTypeId: number, groupId: number | undefined) => void;
  /** New groups are created right away, so only when there is no draft. */
  canCreateGroup: boolean;
  onCreateGroup: () => void;
}

const geoOptions = Object.entries(GEO_LABELS);

/** Product groups: which products a group holds, and its rules per plan. */
export function ProductGroups({
  draft,
  productTypes,
  selectedGroupId,
  onSelectGroup,
  onGroupChange,
  onRuleChange,
  onMoveProduct,
  canCreateGroup,
  onCreateGroup,
}: ProductGroupsProps) {
  const group = draft.groups.find((item) => item.id === selectedGroupId) ?? draft.groups[0];

  if (!group) {
    return <Alert severity="info">No product groups yet.</Alert>;
  }

  const productName = (id: number) => productTypes.find((item) => item.id === id)?.name ?? `Product #${id}`;
  const ownerOf = (id: number) => draft.groups.find((item) => item.productTypeIds.includes(id));
  const addable = productTypes.filter((item) => !group.productTypeIds.includes(item.id));

  const patchRule = (planId: number, patch: Partial<GroupRuleValues>) => {
    const current = draft.groupRules[ruleKey(planId, group.id)];

    if (current) {
      onRuleChange(planId, group.id, { ...current, ...patch });
    }
  };

  const ruleRow = (
    label: string,
    help: string,
    renderCell: (planId: number, rule: GroupRuleValues) => React.ReactNode,
    showAddButton = false,
  ) => (
    <TableRow>
      <TableCell sx={{ width: 190, verticalAlign: 'top' }}>
        <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
        <Typography variant="caption" sx={{ color: COLORS.LIGHT_GRAY }}>
          {help}
        </Typography>
      </TableCell>
      {draft.plans.map((plan) => {
        const rule = draft.groupRules[ruleKey(plan.id, group.id)];

        return (
          <TableCell key={plan.id} sx={{ verticalAlign: 'top' }}>
            {rule && renderCell(plan.id, rule)}
            {!rule && showAddButton && (
              <Box>
                <Typography sx={{ color: COLORS.LIGHT_GRAY, fontStyle: 'italic' }}>
                  No rules (open to everyone)
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  sx={{ mt: '6px' }}
                  onClick={() => onRuleChange(plan.id, group.id, { ...OPEN_GROUP_RULE })}
                >
                  Add rules for this plan
                </Button>
              </Box>
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );

  return (
    // Groups sit in a row above the card, so the rules table gets the full width.
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: '8px' }}>
        <List dense sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', p: 0 }} aria-label="Product groups">
          {draft.groups.map((item) => (
            <ListItemButton
              key={item.id}
              selected={item.id === group.id}
              onClick={() => onSelectGroup(item.id)}
              sx={{
                flex: '0 0 auto',
                minWidth: '160px',
                borderRadius: '8px',
                border: `1px solid ${alpha(COLORS.MAIN_DARK, 0.08)}`,
              }}
            >
              <ListItemText
                primary={`${item.name}${item.active ? '' : ' (off)'}`}
                secondary={`${item.productTypeIds.length} product${item.productTypeIds.length === 1 ? '' : 's'}`}
              />
            </ListItemButton>
          ))}
        </List>
        <Tooltip title={canCreateGroup ? '' : 'Publish or discard your changes first'}>
          <Box component="span" sx={{ display: 'flex' }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{ minWidth: '160px', borderStyle: 'dashed' }}
              disabled={!canCreateGroup}
              onClick={onCreateGroup}
            >
              New group
            </Button>
          </Box>
        </Tooltip>
      </Box>

      <SectionCard
        title={group.name}
        description="Rules for these products, per plan."
        action={
          <FormControlLabel
            control={
              <Switch
                checked={group.active}
                onChange={(event) => onGroupChange(group.id, { active: event.target.checked })}
              />
            }
            label={group.active ? 'Rules switched on' : 'Rules off: no limits'}
          />
        }
      >
        <Typography sx={{ fontWeight: 600, mb: '8px' }}>Products in this group</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
          {group.productTypeIds.map((id) => (
            <Chip
              key={id}
              label={productName(id)}
              color="primary"
              variant="outlined"
              onDelete={() => onMoveProduct(id, undefined)}
            />
          ))}
          {!group.productTypeIds.length && (
            <Typography sx={{ color: COLORS.LIGHT_GRAY, fontStyle: 'italic' }}>No products yet</Typography>
          )}
          <Select
            size="small"
            displayEmpty
            value=""
            onChange={(event) => onMoveProduct(Number(event.target.value), group.id)}
            renderValue={() => '+ Add product'}
            sx={{ minWidth: 160 }}
            inputProps={{ 'aria-label': 'Add product' }}
          >
            {addable.map((item) => {
              const owner = ownerOf(item.id);

              return (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                  <Typography component="span" variant="caption" sx={{ ml: 1, color: COLORS.LIGHT_GRAY }}>
                    {owner ? `(in ${owner.name})` : '(no group)'}
                  </Typography>
                </MenuItem>
              );
            })}
          </Select>
        </Box>
        <Typography sx={{ fontSize: '0.8125rem', color: COLORS.LIGHT_GRAY, mt: '6px', mb: '16px' }}>
          A product can be in one group only. Adding one from another group moves it here.
        </Typography>

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label={`${group.name} rules per plan`}>
            <TableHead>
              <TableRow>
                <TableCell />
                {draft.plans.map((plan) => (
                  <TableCell key={plan.id} sx={{ fontWeight: 600 }}>
                    {planLabel(plan)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {ruleRow(
                'Portfolios',
                'in this group',
                (planId, rule) => (
                  <LimitControl
                    ariaLabel="Portfolios in this group"
                    value={rule.portfolioLimit}
                    onChange={(value) => patchRule(planId, { portfolioLimit: value })}
                  />
                ),
                true,
              )}
              {ruleRow('Saved recommendations', 'in this group', (planId, rule) => (
                <Box>
                  <LimitControl
                    ariaLabel="Saved recommendations in this group"
                    value={rule.recommendationLimit}
                    onChange={(value) => patchRule(planId, { recommendationLimit: value })}
                  />
                  <FormControlLabel
                    sx={{ mt: '4px' }}
                    control={
                      <Switch
                        size="small"
                        checked={rule.recommendationCappedByPortfolios}
                        onChange={(event) =>
                          patchRule(planId, { recommendationCappedByPortfolios: event.target.checked })
                        }
                      />
                    }
                    label={<Typography variant="caption">Count as portfolio slots</Typography>}
                  />
                </Box>
              ))}
              {ruleRow('Funds in one portfolio', 'how many funds', (planId, rule) => (
                <LimitControl
                  ariaLabel="Funds in one portfolio"
                  allowBlocked={false}
                  value={rule.maxFunds}
                  onChange={(value) => patchRule(planId, { maxFunds: value })}
                />
              ))}
              {ruleRow('Fund weights', 'how weights can be set', (planId, rule) => (
                <Select
                  size="small"
                  value={rule.weightsRule}
                  onChange={(event) => patchRule(planId, { weightsRule: event.target.value as WeightsRule })}
                  inputProps={{ 'aria-label': 'Fund weights' }}
                >
                  {(Object.keys(WEIGHTS_LABELS) as WeightsRule[]).map((weightsRule) => (
                    <MenuItem key={weightsRule} value={weightsRule}>
                      {WEIGHTS_LABELS[weightsRule]}
                    </MenuItem>
                  ))}
                </Select>
              ))}
              {ruleRow('Funds from', 'which region', (planId, rule) => (
                <Select
                  size="small"
                  value={String(rule.allowedFundGeoCategoryId)}
                  onChange={(event) =>
                    patchRule(planId, {
                      allowedFundGeoCategoryId: event.target.value === 'null' ? null : Number(event.target.value),
                    })
                  }
                  inputProps={{ 'aria-label': 'Funds from' }}
                >
                  {geoOptions.map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              ))}
              {ruleRow('Remove rules', 'this plan becomes open', (planId) => (
                <Button size="small" color="error" onClick={() => onRuleChange(planId, group.id, undefined)}>
                  Remove rules for this plan
                </Button>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Box
          sx={{
            mt: '16px',
            p: '12px 16px',
            borderRadius: '10px',
            bgcolor: alpha(COLORS.PRIMARY_BLUE, 0.05),
            border: `1px solid ${alpha(COLORS.PRIMARY_BLUE, 0.12)}`,
          }}
        >
          <Typography>
            <Box component="span" sx={{ fontWeight: 700, color: COLORS.PRIMARY_BLUE }}>
              What a free user sees:
            </Box>{' '}
            {freeUserSentence(draft, group)}
          </Typography>
        </Box>
      </SectionCard>
    </Box>
  );
}
